import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';

/**
 * Session-lifecycle signal for the SessionWarningDialog.
 *
 * History: this used to poll the access-token JWT `exp` and warn when within
 * 5 minutes of expiry. That was a pre-OIDC design. Under ZewstID + OIDC,
 * `react-oidc-context` runs `automaticSilentRenew`, so the short-lived access
 * token is renewed silently in the background every cycle. The old polling
 * therefore nagged on essentially every token cycle (the 5-min warning window
 * was ~the whole token lifetime) while the SDK was quietly keeping the session
 * alive — pure noise.
 *
 * Now we stay quiet during normal operation and only raise `isAboutToExpire`
 * when the session genuinely needs the student's attention: silent renewal
 * failed (refresh token / SSO session gone) or the user was signed out
 * server-side. `refresh()` retries silent renew and falls back to an
 * interactive sign-in; `signOut()` clears the user (the route guard then
 * redirects to /login).
 */
export interface SessionExpiryState {
  /** True only when the session can no longer renew itself silently. */
  isAboutToExpire: boolean;
  refresh: () => Promise<void>;
  signOut: () => void;
}

export function useSessionExpiry(): SessionExpiryState {
  const auth = useAuth();
  const [sessionEnded, setSessionEnded] = useState(false);

  useEffect(() => {
    const events = auth.events;
    if (!events) return;

    const onProblem = () => setSessionEnded(true);
    const onRecovered = () => setSessionEnded(false);

    // Normal access-token expiry is auto-renewed by the SDK — we do NOT react
    // to addAccessTokenExpiring/Expired. We only surface a problem when renewal
    // actually fails or the session ends server-side. A successful (re)load of
    // the user clears the flag.
    events.addSilentRenewError(onProblem);
    events.addUserSignedOut(onProblem);
    events.addUserLoaded(onRecovered);

    return () => {
      events.removeSilentRenewError(onProblem);
      events.removeUserSignedOut(onProblem);
      events.removeUserLoaded(onRecovered);
    };
  }, [auth.events]);

  const refresh = useCallback(async () => {
    try {
      await auth.signinSilent();
      setSessionEnded(false);
    } catch {
      // Silent renew is still failing → fall back to interactive sign-in.
      // This navigates away to ZewstID, so control won't return here.
      await auth.signinRedirect();
    }
  }, [auth]);

  const signOut = useCallback(() => {
    // Clear the OIDC user; AppRoutes' guard then redirects to /login.
    void auth.removeUser();
  }, [auth]);

  return {
    isAboutToExpire: sessionEnded,
    refresh,
    signOut,
  };
}
